// src/app/components/PixBimViewer/Components/PixBimViewport.tsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Application, PointData } from "pixi.js";
import type { Viewport as ViewportType } from "pixi-viewport";
import { Viewport } from "pixi-viewport";

// @ts-expect-error no expeted member as pixi/react is in beta
import type { PixiReactElementProps } from "@pixi/react";
import { extend, useApplication } from "@pixi/react";
import type { PixBimViewerAction } from "../PixBimViewer";

declare global {
  namespace JSX {
    interface IntrinsicElements {
      viewport: PixiReactElementProps<typeof ViewportType>;
    }
  }
}

type BoundingBox = {
  x: number;
  y: number;
  width: number;
  height: number;
};

export type IPixBimCameraState = {
  zoom: number;
  centerWorld: { x: number; y: number };
};

export interface ViewportProps {
  onMove?: () => void;
  modelCenter: PointData;
  children?: React.ReactNode;
  boundingBox?: BoundingBox;
  action: PixBimViewerAction;
  moveToBoundingBox?: BoundingBox;
  width?: number;
  height?: number;

  /** Space key down => allow pan with left mouse (Magic Mouse-friendly) */
  panKeyDown?: boolean;

  /** Developer-mode: always pan with left mouse (no Space needed) */
  useLeftClickToPan?: boolean;

  /** Increment to force "zoom extent" even if userZoomed=true */
  fitTrigger?: number;

  // ---------------------------------------------------------------------------
  // ✅ Controlled camera (optional)
  // Hvis undefined => oppførsel som før (intern kamera)
  // ---------------------------------------------------------------------------
  zoom?: number;
  centerWorld?: { x: number; y: number };

  /**
   * ✅ Eneste callback vi emitter fra viewport.
   * Denne blir debouncet og "dedupet" (sendes ikke hvis kamera er likt).
   */
  onCameraChange?: (camera: IPixBimCameraState) => void;

  /** Debounce for camera callbacks (ms). Default 200. */
  cameraDebounceMs?: number;

  /**
   * Hvor "likt" kamera må være for at vi skal ignorere oppdatering.
   * Default er konservativt for å unngå redux-spam.
   */
  cameraEpsilon?: {
    zoom?: number; // default 1e-4
    pos?: number; // default 0.25 (world units)
  };
}

export interface PixiComponentViewportProps extends ViewportProps {
  app: Application;
}

extend({ Viewport });

const defaultEps = {
  zoom: 1e-4,
  pos: 0.25,
};

const approxEqual = (a: number, b: number, eps: number) => Math.abs(a - b) <= eps;

const sameCamera = (
  a: IPixBimCameraState,
  b: IPixBimCameraState,
  eps: Required<typeof defaultEps>
) => {
  return (
    approxEqual(a.zoom, b.zoom, eps.zoom) &&
    approxEqual(a.centerWorld.x, b.centerWorld.x, eps.pos) &&
    approxEqual(a.centerWorld.y, b.centerWorld.y, eps.pos)
  );
};

const PixBimViewport: React.FC<ViewportProps> = (props) => {
  const { app } = useApplication();
  const { renderer, ticker } = app;

  const [viewportRef, setViewportRef] = useState<ViewportType | null>(null);

  const {
    modelCenter,
    boundingBox,
    action,
    moveToBoundingBox,
    onMove,
    fitTrigger,
    panKeyDown = false,
    useLeftClickToPan = false,

    // controlled camera
    zoom,
    centerWorld,
    onCameraChange,
    cameraDebounceMs = 50,
    cameraEpsilon,
    ...rest
  } = props;

  const eps = {
    zoom: cameraEpsilon?.zoom ?? defaultEps.zoom,
    pos: cameraEpsilon?.pos ?? defaultEps.pos,
  };

  const userZoomed = useRef<boolean>(false);
  const lastFitTrigger = useRef<number>(fitTrigger ?? 0);

  // Hindrer feedback-loop når controlled props blir applisert
  const isApplyingControlledCamera = useRef<boolean>(false);

  // Holder siste kamera vi faktisk sendte opp (for dedupe)
  const lastEmittedCameraRef = useRef<IPixBimCameraState | null>(null);

  // ---------------------------------------------------------------------------
  // Init viewport plugins once
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!viewportRef) return;

    // Default = Revit-like: pan with middle mouse drag
    viewportRef.drag({ mouseButtons: "middle" }).pinch().wheel();
  }, [viewportRef]);

  // ---------------------------------------------------------------------------
  // Toggle drag mouse buttons
  // Priority:
  //  1) useLeftClickToPan => always left
  //  2) panKeyDown => left (Space held)
  //  3) otherwise => middle (Revit-like)
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!viewportRef) return;

    // If viewport is paused due to dragSelect, don't modify plugins
    if (action === "dragSelect") return;

    const dragPlugin = viewportRef.plugins.get("drag") as any;
    if (!dragPlugin) return;

    const shouldUseLeft = useLeftClickToPan || panKeyDown;
    dragPlugin.mouseButtons(shouldUseLeft ? "left" : "middle");
  }, [action, panKeyDown, useLeftClickToPan, viewportRef]);

  // ---------------------------------------------------------------------------
  // Pause viewport while drag-selecting
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!viewportRef) return;
    viewportRef.pause = action === "dragSelect";
  }, [action, viewportRef]);

  // ---------------------------------------------------------------------------
  // Zoom extent on mount / bbox changes
  // - auto (only if user hasn't interacted)  => ONLY when NOT controlled
  // - forced when fitTrigger changes         => ALWAYS (also when controlled)
  // ---------------------------------------------------------------------------
  const isControlled = zoom != null || centerWorld != null;

  useEffect(() => {
    if (!viewportRef) return;
    if (!boundingBox) return;

    const currentTrigger = fitTrigger ?? 0;
    const triggerChanged = currentTrigger !== lastFitTrigger.current;

    const shouldAutoFit = !userZoomed.current && !isControlled; // ✅ only uncontrolled
    const shouldForceFit = triggerChanged; // ✅ always

    if (!shouldAutoFit && !shouldForceFit) return;

    const { x, y, width, height } = boundingBox;

    // Reset zoom a bit so fit becomes deterministic
    viewportRef.setZoom(1, true);

    // Put bbox top-left in view, then fit
    viewportRef.moveCorner(x, y);
    viewportRef.fit(true);

    // Center on bbox after fitting
    viewportRef.moveCenter(x + width / 2, y + height / 2);

    // remember trigger
    lastFitTrigger.current = currentTrigger;

    // Etter en fit regner vi dette som "bruker har fått et valgt view"
    userZoomed.current = true;
  }, [boundingBox, fitTrigger, isControlled, viewportRef]);

  // ---------------------------------------------------------------------------
  // Optional: animate camera to a specific polygon bbox
  // ---------------------------------------------------------------------------
  const {
    x: moveToBoundingBoxX,
    y: moveToBoundingBoxY,
    width: moveToBoundingBoxWidth,
    height: moveToBoundingBoxHeight,
  } = moveToBoundingBox || {};

  useMemo(() => {
    if (
      viewportRef &&
      moveToBoundingBoxX != null &&
      moveToBoundingBoxY != null &&
      moveToBoundingBoxWidth != null &&
      moveToBoundingBoxHeight != null
    ) {
      viewportRef.animate({
        time: 300,
        position: {
          x: moveToBoundingBoxX + moveToBoundingBoxWidth / 2 - modelCenter.x,
          y: moveToBoundingBoxY + moveToBoundingBoxHeight / 2 - modelCenter.y,
        },
      });
    }
  }, [
    modelCenter.x,
    modelCenter.y,
    moveToBoundingBoxX,
    moveToBoundingBoxY,
    moveToBoundingBoxWidth,
    moveToBoundingBoxHeight,
    viewportRef,
  ]);

  // ---------------------------------------------------------------------------
  // ✅ Apply controlled zoom/centerWorld when provided
  // IMPORTANT:
  //  - vent til boundingBox finnes (geometri lastet)
  //  - re-apply når boundingBox endrer seg (typisk første gang data kommer)
  // ---------------------------------------------------------------------------
  const bboxKey = boundingBox
    ? `${boundingBox.x},${boundingBox.y},${boundingBox.width},${boundingBox.height}`
    : "no-bbox";

  useEffect(() => {
    if (!viewportRef) return;
    if (!isControlled) return;
    if (!boundingBox) return; // ✅ viktig: ikke apply før vi har geometri

    isApplyingControlledCamera.current = true;

    if (zoom != null) {
      viewportRef.setZoom(zoom, true);
    }
    if (centerWorld) {
      viewportRef.moveCenter(centerWorld.x, centerWorld.y);
    }

    userZoomed.current = true;

    // Oppdater "lastEmitted" til denne posisjonen, så vi ikke sender samme opp igjen
    const c = (viewportRef as any).center as { x: number; y: number } | undefined;
    const appliedCamera: IPixBimCameraState = {
      zoom: viewportRef.scale.x,
      centerWorld: c ? { x: c.x, y: c.y } : { x: modelCenter.x, y: modelCenter.y },
    };
    lastEmittedCameraRef.current = appliedCamera;

    const id = window.setTimeout(() => {
      isApplyingControlledCamera.current = false;
    }, 0);

    return () => window.clearTimeout(id);
  }, [
    bboxKey,
    boundingBox,
    centerWorld?.x,
    centerWorld?.y,
    isControlled,
    modelCenter.x,
    modelCenter.y,
    viewportRef,
    zoom,
  ]);

  // ---------------------------------------------------------------------------
  // ✅ Debounced + deduped camera emission (kun onCameraChange)
  // ---------------------------------------------------------------------------
  const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const computeCamera = useCallback((): IPixBimCameraState | null => {
    if (!viewportRef) return null;

    const nextZoom = viewportRef.scale.x;
    const c = (viewportRef as any).center as { x: number; y: number } | undefined;
    const nextCenter = c ? { x: c.x, y: c.y } : { x: modelCenter.x, y: modelCenter.y };

    return { zoom: nextZoom, centerWorld: nextCenter };
  }, [modelCenter.x, modelCenter.y, viewportRef]);

  const emitCameraIfChanged = useCallback(() => {
    if (!onCameraChange) return;
    if (!viewportRef) return;
    if (isApplyingControlledCamera.current) return;

    const cam = computeCamera();
    if (!cam) return;

    const last = lastEmittedCameraRef.current;
    if (last && sameCamera(cam, last, eps)) return; // ✅ ikke spam

    lastEmittedCameraRef.current = cam;
    onCameraChange(cam);
  }, [computeCamera, eps, onCameraChange, viewportRef]);

  const scheduleEmit = useCallback(() => {
    if (!onCameraChange) return;
    if (!viewportRef) return;
    if (isApplyingControlledCamera.current) return;

    if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);

    debounceTimeoutRef.current = setTimeout(() => {
      emitCameraIfChanged();
    }, cameraDebounceMs);
  }, [cameraDebounceMs, emitCameraIfChanged, onCameraChange, viewportRef]);

  // ---------------------------------------------------------------------------
  // Listen for user interaction
  // - unngå auto-fit etterpå
  // - schedule debounced camera emit
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!viewportRef) return;

    const onZoomed = () => {
      userZoomed.current = true;
      scheduleEmit();
    };

    const onMoved = () => {
      userZoomed.current = true;
      onMove?.();
      scheduleEmit();
    };

    viewportRef.on("zoomed", onZoomed);
    viewportRef.on("moved", onMoved);
    viewportRef.on("pointermove", onMoved);

    return () => {
      viewportRef.off("zoomed", onZoomed);
      viewportRef.off("moved", onMoved);
      viewportRef.off("pointermove", onMoved);

      if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);
    };
  }, [onMove, scheduleEmit, viewportRef]);

  // ---------------------------------------------------------------------------
  // Cleanup
  // ---------------------------------------------------------------------------
  useEffect(() => {
    return () => {
      viewportRef?.destroy();
    };
  }, [viewportRef]);

  if (!renderer) return null;

  return (
    <viewport
      ref={(node: ViewportType) => setViewportRef(node)}
      events={renderer.events}
      ticker={ticker}
      {...rest}
    />
  );
};

export default PixBimViewport;
